/**
 * Per-element entrance row (EA-9; docs/entrance-animations-contracts.md §3).
 *
 * ONE shared row, mounted by the GlassPanel BASE render for every panel whose
 * target carries an eid — the plan named five panels, and the base-class
 * shared-controls seam covers exactly those and cannot drift when a sixth
 * panel appears. Chips derive from ENTRANCE_PRESETS (never a local list),
 * writes go through `applyElementOverrides` so 16C2 fan-out, one-history-entry
 * bulk, collab annotation and undo all apply, and the EA-8 seam turns the
 * write into one scroll-preserving rerender.
 *
 * Disclosure over disabling, both ways (the drawer's posture):
 *  - D8: a template-animated section keeps its own animation while the
 *    convention is on — the value still stores and takes effect after Swap.
 *  - Supersession: any element preset REPLACES the section/site entrance for
 *    this section (contracts §3) — said in the row, not discovered later.
 *  - Fan-out: when the write will cover N similar elements, the row says so;
 *    when their current values DISAGREE, no chip claims to be active.
 */

import { store } from '../core/state.js';
import {
  ENTRANCE_PRESETS,
  ENTRANCE_ELEMENT_FIELD,
  ENTRANCE_ELEMENT_DELAY_FIELD,
  ENTRANCE_DELAY_VALUES,
  effectiveEntranceFor
} from '../shared/entrance-presets.js';
import { sectionTemplateRevealPatterns } from '../export/scroll-animation-shim.js';
import {
  applyElementOverrides,
  expandRestyleTargets,
  resolveSectionByInstance
} from './section-utils.js';
import { renderSegmented } from '../ui/design-widgets.js';
import { createButton } from '../ui/components.js';
import { bridge } from './bridge.js';

const OFF = '__off__';
const AUTO = '__auto__';
const rowRefreshers = new WeakMap();

/** Re-read one mounted row from persisted state after undo/redo/collab. */
export function refreshElementEntranceRow(row) {
  rowRefreshers.get(row)?.();
}

function readEntranceValue(section, eid) {
  const record = section?.overrides?.elements?.[eid];
  return {
    preset:
      typeof record?.[ENTRANCE_ELEMENT_FIELD] === 'string' ? record[ENTRANCE_ELEMENT_FIELD] : '',
    delay: ENTRANCE_DELAY_VALUES.includes(record?.[ENTRANCE_ELEMENT_DELAY_FIELD])
      ? record[ENTRANCE_ELEMENT_DELAY_FIELD]
      : ''
  };
}

/** Read across the SAME targets a write would fan to; null value ⇒ mixed. */
function readAcrossTargets(instanceId, eid) {
  const section = resolveSectionByInstance(instanceId)?.section;
  const targets = expandRestyleTargets(instanceId, eid);
  const values = targets.map((id) => readEntranceValue(section, id));
  const first = values[0] || { preset: '', delay: '' };
  const presetMixed = values.some((v) => v.preset !== first.preset);
  const delayMixed = values.some((v) => v.delay !== first.delay);
  return {
    targets,
    preset: presetMixed ? null : first.preset,
    delay: delayMixed ? null : first.delay
  };
}

/**
 * Mount the row. Returns the root element (or null when data has no eid).
 * Idempotent per panel render — the caller rebuilds the body wholesale.
 */
export function mountElementEntranceRow({ container, data }) {
  if (!container || !data?.eid) return null;
  const resolved = resolveSectionByInstance(data.instanceId);
  // Shared nav/footer deliberately do not participate in entrance v1. Hiding
  // the control at the mount seam prevents a value that normalization would
  // discard and emission would never use.
  if (!resolved?.section || !resolved.sectionPath?.startsWith('pages.')) return null;

  const row = document.createElement('div');
  row.className = 'pb-gp-entrance-row';
  // Label row: the name on the left, the one-shot replay on the right — the
  // panel's field-head primitive, not a bare label with a loose button below.
  const head = document.createElement('div');
  head.className = 'pb-gp-field-head';
  const label = document.createElement('span');
  label.className = 'pb-gp-label';
  label.textContent = 'Entrance';
  head.appendChild(label);
  row.appendChild(head);

  const current = readAcrossTargets(data.instanceId, data.eid);

  // Chips DERIVE from the registry — a hand-added sixth chip is the seed.
  const presetOptions = [
    { value: OFF, label: 'Off' },
    ...ENTRANCE_PRESETS.map((preset) => ({ value: preset.id, label: preset.label }))
  ];
  const presetSeg = renderSegmented({
    field: ENTRANCE_ELEMENT_FIELD,
    ariaLabel: 'Element entrance animation',
    options: presetOptions,
    // A MIXED fan-out claims nothing: no active chip, rather than the first
    // unit's value wearing the crown for all of them.
    value: current.preset === null ? '' : current.preset || OFF,
    onChange: (nextValue) => {
      const preset = nextValue === OFF || !nextValue ? '' : nextValue;
      // Clearing the preset clears the delay with it — a preset-less delay is
      // meaningless and the normalizer would delete it anyway.
      const changes = preset
        ? { [ENTRANCE_ELEMENT_FIELD]: preset }
        : { [ENTRANCE_ELEMENT_FIELD]: '', [ENTRANCE_ELEMENT_DELAY_FIELD]: '' };
      applyElementOverrides(data.instanceId, data.eid, changes, {
        label: 'Element entrance',
        source: 'entrance-row'
      });
      refreshControls();
    }
  });
  row.appendChild(presetSeg);

  // Delay: Auto is ABSENCE — `''` deletes the field through hasValue().
  const delayRow = document.createElement('div');
  delayRow.className = 'pb-gp-entrance-delay-row';
  const delayLabel = document.createElement('span');
  delayLabel.className = 'pb-gp-label';
  delayLabel.textContent = 'Delay';
  delayRow.appendChild(delayLabel);
  const delaySeg = renderSegmented({
    field: ENTRANCE_ELEMENT_DELAY_FIELD,
    ariaLabel: 'Element entrance delay',
    options: [
      { value: AUTO, label: 'Auto' },
      ...ENTRANCE_DELAY_VALUES.map((ms) => ({ value: String(ms), label: `${ms}` }))
    ],
    value: current.delay === null ? '' : current.delay === '' ? AUTO : String(current.delay),
    onChange: (nextValue) => {
      const delay =
        nextValue === AUTO || !nextValue
          ? ''
          : ENTRANCE_DELAY_VALUES.includes(Number(nextValue))
            ? Number(nextValue)
            : '';
      applyElementOverrides(
        data.instanceId,
        data.eid,
        { [ENTRANCE_ELEMENT_DELAY_FIELD]: delay },
        { label: 'Element entrance delay', source: 'entrance-row' }
      );
      refreshControls();
    }
  });
  delayRow.appendChild(delaySeg);
  row.appendChild(delayRow);

  // EA-10: one-shot replay scoped to exactly THIS element's participant.
  const previewBtn = createButton({
    label: 'Preview',
    variant: 'bare',
    className: 'pb-gp-entrance-preview',
    tooltip: 'Play this entrance once on the canvas',
    onClick: () => bridge.previewEntrance({ instanceId: data.instanceId, eid: data.eid })
  });
  head.appendChild(previewBtn);

  const notes = document.createElement('div');
  notes.className = 'pb-gp-entrance-notes';
  row.appendChild(notes);

  function refreshDelayRow() {
    const now = readAcrossTargets(data.instanceId, data.eid);
    delayRow.style.display = now.preset ? '' : 'none';
  }

  function refreshNotes() {
    notes.replaceChildren();
    const state = store.getState();
    const now = readAcrossTargets(data.instanceId, data.eid);
    const liveSection = resolveSectionByInstance(data.instanceId)?.section;
    const noteLines = [];
    if (now.targets.length > 1) {
      noteLines.push(
        now.preset === null || now.delay === null
          ? `Mixed across ${now.targets.length} similar elements — picking a value sets all of them.`
          : `Applies to ${now.targets.length} similar elements.`
      );
    }
    if (
      state?.entrance?.template !== false &&
      sectionTemplateRevealPatterns(liveSection).length > 0
    ) {
      noteLines.push(
        'This section already animates itself, so it keeps its own animation. ' +
          "Turn the template's scroll reveal off in Buttons & Effects to use this."
      );
    } else if (now.preset && effectiveEntranceFor(state, liveSection)) {
      noteLines.push("Replaces this section's entrance animation.");
    }
    for (const text of noteLines) {
      const p = document.createElement('p');
      p.className = 'pb-feature-desc';
      p.textContent = text;
      notes.appendChild(p);
    }
  }

  function refreshControls() {
    const now = readAcrossTargets(data.instanceId, data.eid);
    presetSeg.updateValue(now.preset === null ? '' : now.preset || OFF);
    delaySeg.updateValue(now.delay === null ? '' : now.delay === '' ? AUTO : String(now.delay));
    refreshDelayRow();
    refreshNotes();
  }

  rowRefreshers.set(row, refreshControls);
  refreshControls();
  container.appendChild(row);
  return row;
}
